import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch  } from "react-redux";
import Multiselect from "multiselect-react-dropdown";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import { Input, Row, Col, Card, CardBody, Button, Spinner } from "reactstrap";
import { uploadTopicsImageIcon } from "../../../slices/topics/thunk";
import { clearUploadedTopicImageUrl } from "../../../slices/topics/reducer";
import "../../../App.css"
import { uploadReferrelDoc } from "../../../slices/thunks";
import { clearUploadedReferralDocUrl } from "../../../slices/referralReward/reducer";
import { toast } from "react-toastify";

// Helper function to format file sizes
function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

const AddReferralRewardForm = ({
    labels,
    formikProps,
    isAddOrEdit,
    onTranslateClickHandler,
    translationLoading,
    lastTranslatedField,
    setParentSubmitDisabled
}) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === "rtl";

    const [filePreviews, setFilePreviews] = useState({});
    
    const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formikProps;

    // Selectors for data needed by dropdowns, etc.
    const { UploadedDocUrl, DocLoad, country } = useSelector((state) => ({
        UploadedDocUrl: state.ReferralReward.uploadedDocUrl, 
        DocLoad: state.ReferralReward.docLoad, 
        country: state.Module.country,
    }));
    
    const topicIconFieldName = useMemo(() => labels.find(l => l.fieldName === 'file')?.name, [labels]);

    useEffect(() => {
        // Disable parent submit button if an image is currently loading
        if (setParentSubmitDisabled) {
            setParentSubmitDisabled(DocLoad);
        }
    }, [DocLoad, setParentSubmitDisabled]);

    useEffect(() => {
        // When a new image URL is available from Redux store (after upload)
        if (UploadedDocUrl && topicIconFieldName && values[topicIconFieldName] instanceof File) {
            setFieldValue(topicIconFieldName, UploadedDocUrl); 
            dispatch(clearUploadedReferralDocUrl()); 
        }
    }, [UploadedDocUrl, dispatch, setFieldValue, values, topicIconFieldName]);



    const today = new Date().toISOString().split("T")[0];



    useEffect(() => {
        // Cleanup object URLs
        return () => {
            Object.values(filePreviews).forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [filePreviews]);

    const handleFileDrop = (acceptedFiles, fieldName) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];

            // Check if the file is a PDF
            if (file.type !== "application/pdf") {
                toast.warn(t("OnlyPDFsAllowed", "Only PDF files are allowed."));
                setFieldValue(fieldName, null); // Clear the invalid file from Formik
                setFilePreviews(prev => { // Clear preview
                    if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                        URL.revokeObjectURL(prev[fieldName]);
                    }
                    const newState = {...prev};
                    delete newState[fieldName];
                    return newState;
                });
                return; // Stop processing if not a PDF
            }

            setFieldValue(fieldName, file); // Store the File object in Formik

            // Manage preview URL
            setFilePreviews(prev => {
                if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                    URL.revokeObjectURL(prev[fieldName]);
                }
                return { ...prev, [fieldName]: URL.createObjectURL(file) };
            });
            const reader = new FileReader();
            reader.readAsDataURL(file);
                reader.onload = () => {
                    const imageDataUrl = reader.result;
                    const base64Image = imageDataUrl.split(',')[1]; // Get only the base64 part
                    dispatch(uploadReferrelDoc({ ImageData: base64Image, folderName: 'ReferralDocuments' }))
                        .unwrap()
                        .then((uploadedUrl) => {
                            // The useEffect listening to `uploadedImageUrl` will handle setting this URL
                            // to the formik field value and clearing `uploadedImageUrl` from redux state.
                        })
                        .catch(error => {
                            console.error("Image upload error in handleFileDrop:", error);
                            toast.error(t("ImageUploadFailed") + (error.message ? `: ${error.message}` : ''));
                            setFieldValue(fieldName, null); 
                            setFilePreviews(prev => { 
                                if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                                    URL.revokeObjectURL(prev[fieldName]);
                                }
                                const newState = {...prev};
                                delete newState[fieldName];
                                return newState;
                            });
                        });
                };
                reader.onerror = (error) => {
                    console.error("FileReader error:", error);
                    toast.error(t("FileReadError"));
                    setFieldValue(fieldName, null); 
                };
        }
    };

    const renderField = (item) => {
        const fieldName = item.name;
        const fieldValue = values[fieldName] ?? '';
        // Determine if field should be disabled
        const isDisabled = typeof item.disabled === 'function' ? item.disabled(isAddOrEdit, values) : !!item.disabled;

        switch (item.fieldName) {
            case 'text-box':
                return (
                    <Input
                        type="text"
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'text-box-translate':
            case 'textarea-translate':
                const InputComponent = item.fieldName === 'textarea-translate' || item.isTextArea ? 'textarea' : 'text';

                return (
                    <div className={`input-group ${isRTL ? 'rtl-input-group' : ''}`}>
                        <Input
                            type={InputComponent === 'textarea' ? 'textarea' : 'text'}
                            rows={item.fieldName === 'textarea-translate' || item.isTextArea ? (item.rows || "2") : undefined}
                            id={fieldName}
                            name={fieldName}
                            placeholder={t(item.placeholder || item.labelName)}
                            value={fieldValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form-control"
                            invalid={touched[fieldName] && !!errors[fieldName]}
                            disabled={isDisabled}
                            required={item.required}
                        />
                    {onTranslateClickHandler && item.translateTo && (
                        <Button
                            type="button"
                            onClick={() => onTranslateClickHandler(item.translateTo, fieldValue, setFieldValue, item.name)}
                            disabled={!fieldValue || !fieldValue.trim() || translationLoading}
                        >
                            {translationLoading && lastTranslatedField === item.name ? (
                            <Spinner size="sm" />
                            ) : (
                            <i className="ri-translate"></i>
                            )}
                        </Button>
                    )}
                    </div>
                );

            case 'textarea-box':
                return (
                    <Input
                        type="textarea" // reactstrap Input uses this for textarea
                        rows={item.rows || "2"}
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'number-box':
                return (
                    <Input
                        type="text"
                        style={{ textAlign: isRTL ? 'right' : 'left' }}
                        id={item.name}
                        name={item.name}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            let filteredValue = inputValue.replace(/[^0-9.]/g, '');
        
                            const parts = filteredValue.split('.');
                            if (parts.length > 2) {
                                filteredValue = parts[0] + '.' + parts.slice(1).join('');
                            }
                            setFieldValue(item.name, filteredValue);
                        }}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[item.name] && !!errors[item.name]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'check-box':
                return (
                    <div className="form-check "> 
                        <Input
                            type="switch"
                            style={{ padding: '8px 15px' }}
                            id={fieldName}
                            name={fieldName}
                            checked={!!fieldValue} 
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form-check-input"
                            disabled={isDisabled}
                            required={item.required} 
                        />
                    </div>
                );

            case 'date':
                return (
                    <Input
                        type="date"
                        id={fieldName}
                        name={fieldName}
                        min={item.minToday ? today : item.min}
                        max={item.max}
                        value={(fieldValue || '').split('T')[0]} // Ensure date format yyyy-mm-dd
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'referral-dropdown':
                return (
                    <Input
                        type="select"
                        id={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    >
                        <option value="">{t(item.defaultOptionLabel || "Choose")}</option>
                        {(item.options || ReferralReward || []).map((option) => (
                            <option
                                key={option[item.optionValueKey || 'ID']}
                                value={option[item.optionValueKey || 'ID']}
                            >
                                {i18n?.language === 'en'
                                    ? option[item.optionLabelKeyEn || 'ReferralNameEn']
                                    : option[item.optionLabelKeyAr || 'ReferralNameAr']}
                            </option>
                        ))}
                    </Input>
                );

            case 'multiselect-dropdown':
                    const selectedDayValues = country?.filter(day => (values[fieldName] || []).includes(day.ID)) || [];
                    return (
                        <>
                            <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                                {(() => {
                                    const isInvalid = touched[fieldName] && !!errors[fieldName];
                                    const baseSearchBoxStyles = {
                                        paddingRight: i18n.language === 'ar' ? '30px' : '',
                                        textAlign: i18n.language === 'ar' ? 'right' : 'left',
                                    };
                                    if (isInvalid) {
                                        baseSearchBoxStyles.borderColor = '#dc3545'; // Bootstrap's danger color
                                    }
                                    const multiselectCustomStyles = {
                                        multiselectContainer: { textAlign: i18n.language === 'ar' ? 'right' : 'left', direction: i18n.language === 'ar' ? 'rtl' : 'ltr' },
                                        searchBox: baseSearchBoxStyles,
                                        chips: { direction: i18n.language === 'ar' ? 'rtl' : 'ltr', justifyContent: i18n.language === 'ar' ? 'flex-end' : 'flex-start' },
                                        optionContainer: { textAlign: i18n.language === 'ar' ? 'right' : 'left' },
                                    };
    
                                return <Multiselect
                                    options={country || []}
                                    selectedValues={selectedDayValues}
                                    onSelect={(selectedList, selectedItem) => {
                                        const currentValues = values[fieldName] || [];
                                        let newValues = currentValues;
                                        if (!currentValues.includes(selectedItem.ID)) {
                                            newValues = [...currentValues, selectedItem.ID];
                                        }
                                        // Set field value and trigger validation (third argument true)
                                        setFieldValue(fieldName, newValues, true);
                                        // Mark field as touched. Validation was requested by setFieldValue.
                                        // The third argument 'false' here means setFieldTouched itself won't trigger another validation.
                                        setFieldTouched(fieldName, true, false);
                                    }}
                                    onRemove={(selectedList, removedItem) => {
                                        const currentValues = values[fieldName] || [];
                                        const newValues = currentValues.filter(id => id !== removedItem.ID);
                                        // Set field value and trigger validation (third argument true)
                                        setFieldValue(fieldName, newValues, true);
                                        // Mark field as touched.
                                        setFieldTouched(fieldName, true, false);
                                    }}
                                    displayValue={i18n.language === 'en' ? 'en' : 'ar'}
                                    placeholder={t(item.placeholder || "SelectCountry")}
                                    disable={isDisabled}
                                    style={multiselectCustomStyles}
                                />
                                })()}
                            </div>
                            {/* Hidden input for Formik validation, if required attribute is needed */}
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={(values[fieldName] || []).length > 0 ? 'selected' : ''}
                                onChange={() => {}} 
                                required={item.required}
                                style={{
                                    opacity: 0,
                                    height: 0,
                                    width: 0,
                                    position: 'absolute',
                                    pointerEvents: 'none',
                                }}
                            />
                        </>
                    );

            case 'file':
                let previewSrc = null;
                let uploadedFileName = '';
                let uploadedFileSize = '';
                const currentFile = values[fieldName];

                 if (typeof currentFile === 'string' && currentFile) { 
                    previewSrc = currentFile;
                    uploadedFileName = currentFile.substring(currentFile.lastIndexOf('/') + 1);
                 } else if (currentFile instanceof File && currentFile.type === "application/pdf") {
                    previewSrc = filePreviews[fieldName]; // Use state for preview URL
                    uploadedFileName = currentFile.name;
                    uploadedFileSize = formatBytes(currentFile.size);
                }
                const isPdf = (typeof currentFile === 'string' && currentFile.toLowerCase().endsWith('.pdf')) ||
                (currentFile instanceof File && currentFile.type === 'application/pdf');
                return (
                    <CardBody className="p-0"> {/* Adjusted padding */}
                       <Dropzone
                            onDrop={(acceptedFiles) => handleFileDrop(acceptedFiles, fieldName)}
                            disabled={isDisabled || DocLoad} // This correctly disables Dropzone during general disable or image loading
                            accept={'.pdf'}                        
                        >
                            {({ getRootProps, getInputProps }) => (
                                <div 
                                    className="dropzone dz-clickable" 
                                    style={{ minHeight: '60px', position: 'relative' }}
                                >                              
                                    <div className="dz-message needsclick" {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        {DocLoad && ( 
                                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'none' }}>
                                                <Spinner size="lg" color="primary" />
                                            </div>
                                        )}
                                        <div style={{ opacity: DocLoad ? 0.5 : 1 }}>
                                            <h5>{t("DropFile")}</h5>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Dropzone>

                        {previewSrc && (
                            <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                                <div className="p-2">
                                    <Row className="align-items-center">
                                        <Col className="col-auto">
                                        {
                                             isPdf ? (
                                                <div className="avatar-sm">
                                                    <span className="avatar-title rounded bg-light text-danger d-flex align-items-center justify-content-center">
                                                        <i className="ri-file-pdf-line" style={{ fontSize: '2.5rem' }}></i>
                                                    </span>
                                                </div>
                                            ) : (
                                                <img
                                                    data-dz-thumbnail=""
                                                    height="80"
                                                    className="avatar-sm rounded bg-light"
                                                    src={previewSrc} 
                                                    alt={uploadedFileName || "preview"}
                                                />
                                            )
                                        }
                                        </Col>
                                        <Col>
                                            <Link to="#" className="text-muted font-weight-bold">
                                                {uploadedFileName}
                                            </Link>
                                            {uploadedFileSize && <p className="mb-0"><strong>{uploadedFileSize}</strong></p>}
                                        </Col>
                                        {/* Optional: Button to clear the file */}
                                        {!isDisabled && (values[fieldName]) && (
                                            <Col className="col-auto">
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        setFieldValue(fieldName, null); // Clear Formik value
                                                        setFilePreviews(prev => {
                                                            if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                                                                URL.revokeObjectURL(prev[fieldName]);
                                                            }
                                                            const newState = {...prev};
                                                            delete newState[fieldName];
                                                            return newState;
                                                        });
                                                    }}
                                                >
                                                    <i className="ri-close-line"></i>
                                                </Button>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            </Card>
                        )}
                    </CardBody>
                );

            default:
                return <p>Unsupported field type: {item.fieldName}</p>;
        }
    };

    return (
        <Row>
            {labels.map((item) => {
                const fieldName = item.name;
                const showLabel = item.fieldName !== 'check-box' || !item.noMainLabel;

                return (
                    <Col sm={item.colSize || 6} key={fieldName} className={item.className}>
                        <div className="mb-3">
                            {showLabel && (
                                <label htmlFor={fieldName} className="col-form-label">
                                    {t(item.labelName)}
                                    {item.required && <span className="text-danger"> *</span>}
                                </label>
                            )}
                            {renderField(item)}
                            {touched[fieldName] && errors[fieldName] && (
                                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                                    {/* Ensure errors are translated if they are keys */}
                                    {typeof errors[fieldName] === 'string' ? t(errors[fieldName]) : errors[fieldName]}
                                </div>
                            )}
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export default AddReferralRewardForm;